import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { UiTheme } from '../../../constants/ui-theme.type';

// Themes with a hand-written, Bootstrap-free CSS implementation in
// btn-icon.component.scss. Any other theme falls back to the legacy
// `btn btn-{theme}` Bootstrap classes stacked on top of the boxed variant,
// unchanged, so consumers using a theme we haven't migrated/verified yet
// keep their exact current rendering.
const BOOTSTRAP_FREE_ICON_THEMES = new Set<UiTheme>(['outline-secondary', 'outline-danger']);

@Component({
  selector: 'app-btn-icon',
  imports: [],
  templateUrl: './btn-icon.component.html',
  styleUrl: './btn-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BtnIconComponent {
  readonly ariaLabel = input.required<string>();
  readonly action = output<void>();
  readonly disabled = input<boolean>(false);
  readonly theme = input<UiTheme>('');
  readonly type = input<'button' | 'submit'>('button');
  // Escape hatch for one-off modifier classes that need to land on the
  // actual clickable surface, not the outer host.
  readonly extraClass = input<string>('');
  // Renders a Bootstrap-close-glyph-style icon instead of an ng-content icon,
  // for dismiss buttons (e.g. modal headers) that don't have an inner icon
  // element to project.
  readonly close = input<boolean>(false);

  onClick(): void {
    if (this.disabled()) return;
    this.action.emit();
  }

  protected readonly buttonClass = computed(() => {
    const theme = this.theme();
    let base: string;
    if (this.close()) {
      base = 'btn-icon--close';
    } else if (!theme) {
      base = 'btn-icon btn-icon--bare';
    } else if (BOOTSTRAP_FREE_ICON_THEMES.has(theme)) {
      base = `btn-icon btn-icon--boxed btn-icon--${theme}`;
    } else {
      base = `btn btn-${theme} btn-icon btn-icon--boxed`;
    }
    return this.extraClass() ? `${base} ${this.extraClass()}` : base;
  });
}
